/**
 * Pure keyboard utilities used by `MainPage`. Lives here so the
 * tests don't have to import the whole page module.
 */

/**
 * Returns true if `e` is the platform-correct leader chord for
 * summoning the column (PRD docs/prd-keyboard.md §5.8).
 *
 *   - Windows: `Alt+Shift+K` (the `windows` slot in the extension
 *     manifest)
 *   - macOS:   `Cmd+Shift+K` or `Ctrl+Shift+K` (web honours both so
 *     muscle memory carries across surfaces — the extension's `mac`
 *     slot uses Command)
 *   - Linux / ChromeOS / anything else: `Ctrl+Shift+K`
 *
 * `platform` should be the raw platform string, e.g.
 * `navigator.userAgentData?.platform ?? navigator.platform`. The
 * helper upper-cases it internally so the caller doesn't have to.
 */
export function isLeaderChordEvent(e: KeyboardEvent, platform: string): boolean {
  // Browser key-repeat fires while the chord is held. Without this
  // guard `focusSignal` increments many times per hold, snapping
  // selection back to the first unread on every repeat tick. The
  // extension's `commands.onCommand` doesn't repeat — this only
  // matters in the webapp.
  if (e.code !== "KeyK" || !e.shiftKey || e.repeat) return false;
  const plat = platform.toUpperCase();
  if (plat.includes("WIN")) {
    return e.altKey && !e.ctrlKey && !e.metaKey;
  }
  if (plat.includes("MAC")) {
    // XOR: Cmd+Shift+K *or* Ctrl+Shift+K, not both pressed
    // simultaneously. Practically unreachable by accident but
    // documents the spec/manifest intent (one chord at a time).
    // Asymmetry vs. the extension's `mac` manifest slot (Command
    // only) is intentional — see `apps/extension/public/manifest.json`
    // and PRD §5.8 for the full rationale.
    return (e.metaKey ? !e.ctrlKey : e.ctrlKey) && !e.altKey;
  }
  // Linux / ChromeOS / fallback → Ctrl+Shift+K (the manifest's
  // `default`). `navigator.userAgentData.platform` on ChromeOS
  // returns "Chrome OS" (with a space); `navigator.platform`
  // returns "CrOS x86_64". Neither contains "WIN" or "MAC", so
  // both fall through here.
  return e.ctrlKey && !e.altKey && !e.metaKey;
}

/**
 * Install the document-level leader-chord listener used by the
 * webapp's summon flow. Returns a teardown function suitable for a
 * React `useEffect` cleanup. Extracted from `MainPage` so the
 * keydown → input-trap → focusSignal wiring can be unit-tested
 * without rendering the full page.
 *
 * `shouldFire` is injected so the test can pass an identity
 * function — the production caller wires `shouldFireShortcut` from
 * `@reflecto/client-ui`. `onSummon` is the bump-focus-signal
 * callback.
 */
export function installLeaderChordListener(options: {
  platform: string;
  shouldFire: (e: KeyboardEvent) => boolean;
  onSummon: () => void;
}): () => void {
  const { platform, shouldFire, onSummon } = options;
  const handler = (e: KeyboardEvent) => {
    if (!isLeaderChordEvent(e, platform)) return;
    if (!shouldFire(e)) return;
    e.preventDefault();
    onSummon();
  };
  document.addEventListener("keydown", handler);
  return () => document.removeEventListener("keydown", handler);
}

/**
 * Returns true if `e` is the plain ⌘K/Ctrl+K command-palette chord
 * (docs/superpowers/specs/2026-07-12-extension-command-palette-design.md
 * §11 — web slice). Deliberately excludes `shiftKey` so this never
 * collides with the leader chord above (`isLeaderChordEvent`, which
 * REQUIRES shiftKey): Shift+⌘K stays the summon shortcut, unaffected;
 * plain ⌘K opens the palette. Platform-independent (unlike the leader
 * chord) — Linear/Notion/Slack-web convention is the same combo on every
 * platform, no Windows Alt-key branch to consider.
 */
export function isCommandPaletteChordEvent(e: KeyboardEvent): boolean {
  return e.code === "KeyK" && (e.metaKey || e.ctrlKey) && !e.shiftKey;
}

/**
 * Install the document-level ⌘K/Ctrl+K command-palette listener. Mirrors
 * `installLeaderChordListener`'s shape (extracted for the same reason:
 * unit-testable without rendering the full page) but toggles an
 * open/closed boolean on every matching keydown rather than bumping a
 * counter — a second press closes the palette, matching the extension's
 * `SidePanel` behavior (docs/superpowers/specs/2026-07-12-extension-command-palette-design.md §5).
 */
export function installCommandPaletteListener(options: {
  shouldFire: (e: KeyboardEvent) => boolean;
  onToggle: () => void;
}): () => void {
  const { shouldFire, onToggle } = options;
  const handler = (e: KeyboardEvent) => {
    if (!isCommandPaletteChordEvent(e)) return;
    if (!shouldFire(e)) return;
    e.preventDefault();
    onToggle();
  };
  document.addEventListener("keydown", handler);
  return () => document.removeEventListener("keydown", handler);
}
